import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { cn } from '../lib/cn'
import { DropdownChevron, DropdownPanel } from './DropdownPanel'
import { Icons } from '../icons/registry'

export interface DropdownOption<T extends string> {
  value: T
  label: string
  /** A quieter second line under the label, for options that need a word of why. */
  description?: string
  /** Rendered, but not choosable and not reachable by the arrows. */
  disabled?: boolean
}

/**
 * The studio's dropdown — every choice the native `select` used to leave to
 * the operating system, drawn by the design language instead.
 *
 * ---- The anatomy -----------------------------------------------------------
 *
 * The trigger wears the field treatment (`wf-field`), so a form reads as one
 * system whether a row holds text or a choice; its chevron turns as the panel
 * arrives. The panel is a chip on the surface — hairline edge, chip shadow,
 * the menu radius — opening with the vocabulary's own fade, flipping above the
 * trigger when the viewport has no room below. The chosen option carries a
 * check; the row under the pointer or the arrows carries the tint.
 *
 * ---- The keyboard ----------------------------------------------------------
 *
 * Enter, Space or an arrow opens the panel at the chosen option. The arrows
 * walk the choosable rows (disabled ones are visible but skipped), Enter or
 * Space commits, Escape leaves with the choice unchanged and focus back on
 * the trigger, Tab walks on and closes, and a letter jumps to the next label
 * that starts with it. Nothing here depends on the pointer.
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  emptyLabel = 'No options available',
  disabled,
  id,
  ariaLabel,
  className,
  menuWidth = 'field',
  icon,
  triggerWidth = 'field',
}: {
  value: T | ''
  options: readonly DropdownOption<T>[]
  onChange: (value: T) => void
  /** Shown on the trigger when the value matches no option — "Choose a kind". */
  placeholder?: string
  emptyLabel?: string
  disabled?: boolean
  /** The id for the Field label's htmlFor. */
  id?: string
  ariaLabel?: string
  className?: string
  /** Compact menus are capped at 240px while the field keeps its normal width. */
  menuWidth?: 'field' | 'compact'
  /** Optional leading mark in the trigger. */
  icon?: ReactNode
  /** `field` stretches the trigger across its row; `auto` fits it to the value. */
  triggerWidth?: 'field' | 'auto'
}) {
  const listboxId = useId()
  const [open, setOpen] = useState(false)
  const [flip, setFlip] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280, maxHeight: 288 })
  const panelRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const typeBuffer = useRef<{ text: string; at: number }>({ text: '', at: 0 })
  /** The row the arrows or the type-ahead left the panel at. */
  const activeIndexRef = useRef<number | null>(null)

  const selected = options.find((option) => option.value === value)
  const choosable = options.map((option, index) => (option.disabled ? -1 : index)).filter((index) => index >= 0)

  const openPanel = () => {
    if (disabled) return
    setOpen(true)
    const current = options.findIndex((option) => option.value === value)
    const start = current >= 0 ? current : (choosable[0] ?? null)
    // The panel renders after the state lands; focus inside the painted frame.
    requestAnimationFrame(() => {
      if (start !== null) optionRefs.current[start]?.focus({ preventScroll: true })
    })
  }

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const margin = 12
      const below = window.innerHeight - rect.bottom - margin - 8
      const above = rect.top - margin - 8
      const height = Math.min(panelRef.current?.scrollHeight || 288, 288)
      const opensAbove = below < height && above > below
      const maxHeight = Math.max(0, Math.min(288, opensAbove ? above : below))
      const width = Math.min(menuWidth === 'compact' ? Math.min(240, rect.width) : rect.width, window.innerWidth - margin * 2)
      setFlip(opensAbove)
      setPosition({ width, maxHeight,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        top: opensAbove ? Math.max(margin, rect.top - Math.min(height, maxHeight) - 8) : rect.bottom + 8,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, options, menuWidth])

  const commit = (option: DropdownOption<T>) => {
    if (option.disabled) return
    onChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const walk = (from: number, step: 1 | -1): number => {
    const at = choosable.indexOf(from)
    const next = choosable[(at + step + choosable.length) % choosable.length]
    return next ?? from
  }

  const typeJump = (letter: string): number | null => {
    const now = Date.now()
    typeBuffer.current = now - typeBuffer.current.at < 600
      ? { text: typeBuffer.current.text + letter, at: now }
      : { text: letter, at: now }
    const text = typeBuffer.current.text.toLowerCase()
    const from = activeIndexRef.current ?? -1
    const ordered = [...choosable.filter((index) => index > from), ...choosable.filter((index) => index <= from)]
    return ordered.find((index) => options[index]!.label.toLowerCase().startsWith(text)) ?? null
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node) && !panelRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className={cn('relative', triggerWidth === 'auto' && 'inline-block max-w-full', className)}>
      <button
        type="button"
        ref={triggerRef}
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={cn('wf-field flex cursor-pointer items-center justify-between gap-2 text-left', triggerWidth === 'auto' && 'w-fit min-w-36 max-w-full', disabled && 'pointer-events-none')}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {icon ? <span aria-hidden className="inline-flex shrink-0 text-muted-foreground">{icon}</span> : null}
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>{selected?.label ?? placeholder ?? '—'}</span>
        </span>
        <DropdownChevron open={open} />
      </button>

      {createPortal(<AnimatePresence>
        {open ? (
          <DropdownPanel
            id={listboxId}
            ref={panelRef}
            data-wf-popover
            role="listbox"
            aria-label={ariaLabel ?? 'Options'}
            above={flip}
            style={position}
            className="wf-frost fixed z-[200] overflow-y-auto overscroll-contain rounded-xl border border-hairline p-1 shadow-overlay"
            onKeyDown={(event) => {
              const current = activeIndexRef.current
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                const step = event.key === 'ArrowDown' ? 1 : -1
                const next = current === null ? (choosable[0] ?? null) : walk(current, step)
                if (next !== null) optionRefs.current[next]?.focus()
              } else if (event.key === 'Home' || event.key === 'End') {
                event.preventDefault()
                const next = event.key === 'Home' ? (choosable[0] ?? null) : (choosable[choosable.length - 1] ?? null)
                if (next !== null) optionRefs.current[next]?.focus()
              } else if (event.key === 'Tab') {
                triggerRef.current?.focus()
                setOpen(false)
              } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
                const match = typeJump(event.key)
                if (match !== null) {
                  event.preventDefault()
                  optionRefs.current[match]?.focus()
                }
              }
            }}
          >
            {!options.length ? <p role="status" className="px-3 py-2 text-caption text-muted-foreground">{emptyLabel}</p> : null}
            {options.map((option, index) => {
              const chosen = option.value === value
              return (
                <div key={option.value} role="none">
                  <button
                    type="button"
                    ref={(node) => {
                      optionRefs.current[index] = node
                    }}
                    role="option"
                    aria-selected={chosen}
                    aria-disabled={option.disabled || undefined}
                    onClick={() => commit(option)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        commit(option)
                      }
                    }}
                    onFocus={() => {
                      activeIndexRef.current = index
                    }}
                    className={cn(
                      'flex min-h-[var(--control-md)] w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-ui outline-none transition-colors duration-fast',
                      'hover:bg-tint focus:bg-tint',
                      chosen && 'bg-tint',
                      option.disabled ? 'cursor-not-allowed text-muted-foreground/60' : 'cursor-pointer text-foreground',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description ? (
                        <span className="block truncate text-caption text-muted-foreground">{option.description}</span>
                      ) : null}
                    </span>
                    {/* The chosen mark rides at the right, past the words — the
                        row reads first and its state is the last thing seen. */}
                    {chosen ? (
                      <span className="shrink-0 self-center text-foreground">
                        <Icons.check />
                      </span>
                    ) : null}
                  </button>
                </div>
              )
            })}
          </DropdownPanel>
        ) : null}
      </AnimatePresence>, document.body)}
    </div>
  )
}
